import { JobDescription as JobDescription, JobQueueInterface, JobType } from "./JobQueueInterface";
import { EventSystem } from "./EventSystem";
import { ModuleLog, getModuleLog } from "./Logger";
import { StorageDb } from "./StorageDb";
import { v4 as uuidv4 } from "uuid";
import { Kernel } from "./Kernel";
import { eachLimit } from "async";
import orderBy from "lodash-es/orderBy.js";
import { formatDateTime } from "./Format";
import throttle from "lodash-es/throttle.js";

type JobEntry = {
    synced: boolean;
};

export type JobProgressEvent = {
    id: string;
    type: JobType;
    name: string;
    count: number;
    index: number;
};

async function getJobTableStorage(name: string, job: JobDescription): Promise<StorageDb<JobEntry>> {
    const tableName = makeJobTableName(name, job);
    const db = new StorageDb<JobEntry>(tableName, Number.MAX_VALUE);
    await db.ready;
    return db;
}

function makeJobTableName(name: string, job: JobDescription) {
    return `${name}-job-${job.id}`;
}

export abstract class PersistentJobQueue extends JobQueueInterface {
    public JOB_CURRENT: string;
    public JOBS_COMPLETED: string;
    public JOBS_OUTSTANDING: string;

    public JOB_PROGRESS_EVENT: string;
    public JOB_COMPLETE_EVENT: string;
    public JOB_ABORTED_EVENT: string;
    public JOB_DELETED_EVENT: string;
    public JOB_STARTED_EVENT: string;
    public JOB_ADDED_EVENT: string;

    private _completedJobs!: StorageDb<JobDescription>;
    private _outstandingJobs!: StorageDb<JobDescription>;
    private _currentJob: JobDescription | undefined;
    private _currentTable: StorageDb<JobEntry> | undefined;
    private _syncPromise: Promise<void> | undefined;
    private _networkStatus = true;
    private _autoSync = true;
    private _cancelSync = false;

    protected readonly _log: ModuleLog;
    protected readonly _events: EventSystem;
    private _throttledUpdateProgress = throttle(
        (e: JobProgressEvent) => this._updateProgress(e),
        250
    );

    constructor(
        private readonly _kernel: Kernel,
        public readonly name: string,
        private readonly _concurrency: number
    ) {
        super();
        this._events = this._kernel.events;
        this.JOB_PROGRESS_EVENT = `${name}-job-progress`;
        this.JOB_STARTED_EVENT = `${name}-job-started`;
        this.JOB_COMPLETE_EVENT = `${name}-job-complete`;
        this.JOB_ABORTED_EVENT = `${name}-job-aborted`;
        this.JOB_DELETED_EVENT = `${name}-job-deleted`;
        this.JOB_ADDED_EVENT = `${name}-job-added`;

        this.JOB_CURRENT = `${name}-job-current`;
        this.JOBS_COMPLETED = `${name}-completed-jobs`;
        this.JOBS_OUTSTANDING = `${name}-outstanding-jobs`;
        this._createJobDbs();
        this._log = getModuleLog(`${name}-browser-dl`);

        this._kernel.events.subscribe("network-status", (active: boolean) => {
            this._networkStatus = active;
            if (active && this._autoSync) {
                this._sync();
            }
        });
    }

    async init(): Promise<void> {
        if (this._autoSync) {
            this._sync();
        }
    }

    private _createJobDbs() {
        this._outstandingJobs = new StorageDb<JobDescription>(
            this.JOBS_OUTSTANDING,
            Number.MAX_VALUE
        );
        this._completedJobs = new StorageDb<JobDescription>(this.JOBS_COMPLETED, Number.MAX_VALUE);
    }

    public async reset(): Promise<void> {
        // stop any sync job
        this._cancelSync = true;
        await this._syncPromise;

        const deleteJobTables = async (jobs: JobDescription[]) => {
            for (const j of jobs) {
                await this._deleteJobCache(j);
            }
        };

        await deleteJobTables(await this._completedJobs.all());
        await deleteJobTables(await this._outstandingJobs.all());

        await this._outstandingJobs.deleteDb();
        await this._completedJobs.deleteDb();
        this._createJobDbs();
    }

    private async _sync() {
        if (this._syncPromise) {
            await this._syncPromise;
        }

        this._syncPromise = this._syncInner();
    }

    protected async _deleteJobCache(job: JobDescription) {
        const dbName = makeJobTableName(this.name, job);
        window.indexedDB.deleteDatabase(dbName);
        await this._completedJobs.delete(job.id);
        await this._outstandingJobs.delete(job.id);
        this._events.raiseEvent(this.JOB_DELETED_EVENT, job);
    }

    protected async _getJob(id: string): Promise<JobDescription | undefined> {
        try {
            return await this._outstandingJobs.get(id);
        } catch (error) {
            // no-op
        }
        try {
            return await this._completedJobs.get(id);
        } catch (error) {
            // no-op
        }
        return undefined;
    }

    private async _syncInner() {
        this._log.debug(`Starting ${this.name} sync`);

        // eslint-disable-next-line no-constant-condition
        while (true) {
            if (this._cancelSync) {
                this._log.debug(`Sync was cancelled`);
                this._currentJob = undefined;
                this._currentTable = undefined;
                window.localStorage.removeItem(this.JOB_CURRENT);
                this._cancelSync = false;
            }

            // process deletes before anything else
            const allQueued = orderBy(await this._outstandingJobs.all(), (x) =>
                x.type && x.type === JobType.Delete ? 0 : 1
            );

            if (allQueued.length === 0) {
                this._log.debug(`No jobs`);
                return;
            }
            const hasDeletes = allQueued.length > 0 && allQueued[0].type === JobType.Delete;

            this._log.debug(`Have ${allQueued.length} jobs`);

            // see if we have an existing job in progress
            if (!hasDeletes) {
                const existingId = window.localStorage.getItem(this.JOB_CURRENT);
                if (existingId) {
                    try {
                        this._currentJob = await this._outstandingJobs.get(existingId);
                        this._currentTable = await getJobTableStorage(this.name, this._currentJob);
                        this._log.debug(`Restarting existing job : ${existingId}`);
                    } catch (error) {
                        window.localStorage.removeItem(this.JOB_CURRENT);
                    }
                }
            }

            if (!this._currentJob) {
                const top = allQueued[0];
                this._currentJob = top;
                this._log.debug(`Queuing next job : ${top.type}`);

                if (top.type === JobType.Delete) {
                    // job maybe still outstanding or completed
                    const syncJob = await this._getJob(top.userData.jobId);
                    if (syncJob) {
                        this._currentTable = await getJobTableStorage(this.name, syncJob);
                    } else {
                        // corresponding job does not exist
                        await this._outstandingJobs.delete(top.id);
                        this._currentJob = undefined;
                    }
                } else {
                    this._currentTable = await getJobTableStorage(this.name, this._currentJob);
                }
                if (this._currentJob) {
                    window.localStorage.setItem(this.JOB_CURRENT, this._currentJob.id);
                    this._log.debug(`Starting ${this._currentJob.type} :  ${this._currentJob.id}`);
                }
            }
            if (this._currentJob && this._currentTable) {
                this._events.raiseEvent(this.JOB_STARTED_EVENT);
                const result = await this._runJob();
                if (!result) {
                    // stop trying if we are offline and syncing
                    if (!this._networkStatus && this._currentJob.type !== JobType.Delete) {
                        this._events.raiseEvent(this.JOB_ABORTED_EVENT);
                        return;
                    }
                }
            } else {
                return;
            }
        }
    }

    public override async startJob(
        keys: string[],
        description: string,
        userData: any
    ): Promise<JobDescription> {
        // create a table to store the job entries in
        const jobDesc = {
            id: uuidv4(),
            type: JobType.Sync,
            numTiles: keys.length,
            description,
            date: formatDateTime(),
            userData,
        } as JobDescription;

        const table = await getJobTableStorage(this.name, jobDesc);
        await table.setMultiple(
            keys.map((x) => {
                return { key: x, value: { synced: false } };
            })
        );
        await this._outstandingJobs.set(jobDesc.id, jobDesc);
        if (this._autoSync) {
            this._sync();
        }
        this._kernel.events.raiseEvent(this.JOB_ADDED_EVENT, jobDesc);
        return jobDesc;
    }

    public override async deleteJob(id: string) {
        // queue up a delete job and cancel the current sync operation
        const jobId = uuidv4();
        await this._outstandingJobs.set(jobId, {
            id: jobId,
            type: JobType.Delete,
            description: `Delete`,
            date: formatDateTime(),
            userData: {
                jobId: id,
            },
            numTiles: 0,
        });
        this._cancelSync = true;
        this._sync();
    }

    private _makeSyncEntry(type: JobType, completed: any) {
        return async (key: string) => {
            if (!this._currentTable) {
                completed(false);
                return;
            }
            const entry = await this._currentTable.get(key);
            if (entry) {
                try {
                    switch (type) {
                        case JobType.Sync:
                            {
                                if (!entry.synced) {
                                    await this.syncEntry(key);
                                    this._log.trace(`Cached ${key}`);
                                    if (!this._cancelSync) {
                                        await this._currentTable.set(key, { synced: true });
                                    }
                                }
                            }
                            break;

                        case JobType.Delete:
                            {
                                await this.removeEntry(key);
                                this._log.trace(`Deleted ${key}`);
                                if (!this._cancelSync) {
                                    await this._currentTable.delete(key);
                                }
                            }
                            break;

                        default:
                            throw new Error("Unsupported job type");
                    }
                    completed(true);
                } catch (error: any) {
                    completed(false);
                    this._log.error(`Failed to cache ${key} : ${error}`);
                }
            } else {
                completed(true);
            }
        };
    }

    private _updateProgress(e: JobProgressEvent) {
        this._events.raiseEvent(this.JOB_PROGRESS_EVENT, e);
    }

    private async _runJob(): Promise<boolean> {
        if (!this._currentTable || !this._currentJob) {
            return true;
        }

        const keys = await this._currentTable.allKeys();
        let numErrors = 0;
        if (keys) {
            this._kernel.preventScreenTurningOff();
            let index = 0;

            try {
                await new Promise((resolve, reject) => {
                    if (!this._currentJob) {
                        return;
                    }
                    eachLimit(
                        keys,
                        this._concurrency,
                        this._makeSyncEntry(this._currentJob?.type, (success: boolean) => {
                            if (success && this._currentJob) {
                                this._throttledUpdateProgress({
                                    id: this._currentJob.id,
                                    type: this._currentJob.type,
                                    name: this._currentJob.description,
                                    index,
                                    count: keys.length,
                                });
                                ++index;
                            } else {
                                ++numErrors;
                            }
                            // Bail if we have lost network connectivity. This can also happen on success
                            // if we are hitting the cache syncing date.
                            if (!this._networkStatus && this._currentJob?.type !== JobType.Delete) {
                                throw new Error("Lost network");
                            }

                            if (this._cancelSync) {
                                this._log.debug(
                                    `Sync cancelled - aborting ${this._currentJob?.id}`
                                );
                                throw new Error("Sync cancelled");
                            }
                        }),
                        (err) => {
                            if (err) {
                                reject(err);
                            }
                            resolve(undefined);
                        }
                    );
                });
            } catch (_err: any) {
                this._kernel.allowScreenTurningOff();
                return false;
            }

            if (numErrors === 0) {
                const jobId = this._currentJob.id;
                this._log.debug(`${this._currentJob.type} complete for ${jobId}`);

                switch (this._currentJob.type) {
                    case JobType.Delete:
                        {
                            const syncJobId = this._currentJob.userData.jobId;
                            const syncJob = await this._getJob(syncJobId);
                            if (syncJob) {
                                await this._deleteJobCache(syncJob);
                                this._events.raiseEvent(this.JOB_DELETED_EVENT, {
                                    id: syncJob.id,
                                });
                            }
                        }

                        break;

                    case JobType.Sync:
                        {
                            await this._completedJobs.set(jobId, this._currentJob);
                        }
                        break;

                    default:
                        break;
                }
                await this._outstandingJobs.delete(jobId);
                await this.jobCompleted(this._currentJob);

                this._currentJob = undefined;
                this._currentTable = undefined;
            } else {
                this._log.debug(`${this._currentJob.type} had ${numErrors} errors`);
            }
            this._events.raiseEvent(this.JOB_COMPLETE_EVENT);
            this._kernel.allowScreenTurningOff();
        }
        return numErrors === 0;
    }

    public override async completedJobs(): Promise<JobDescription[]> {
        return await this._completedJobs.all();
    }

    public override async outstandingJobs(): Promise<JobDescription[]> {
        return await this._outstandingJobs.all();
    }

    //-------------------------------------------------------------------------
    // Interface
    //-------------------------------------------------------------------------

    protected abstract syncEntry(key: string): Promise<void>;
    protected abstract removeEntry(key: string): Promise<void>;
    protected async jobCompleted(_desc: JobDescription): Promise<void> {}
}
